import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { holdTypesService } from '../../../services/hold-types';
import type { HoldType } from '../../../types/hold-type';
import { ColorSlot } from './ColorSlot';
import { HoldTypeDeleteDialog } from './HoldTypeDeleteDialog';

/** A page of rows: enough to work through, short enough to scan. */
const PAGE_SIZE = 15;
const SEARCH_DEBOUNCE_MS = 250;

interface HoldTypesPanelProps {
  /** Slug or id of the place whose list this is. */
  locationId: string;
}

/**
 * The gym's hold types: what its holds look like, as the list every climb picks
 * from.
 *
 * It replaces text that used to be retyped onto each climb, which is why the
 * count matters enough to sit in the row — a type is not an abstraction here,
 * it is "these 34 problems". It is also the warning before the bin: deleting one
 * leaves those climbs standing without an answer for their holds.
 *
 * The mixed row is the app's rather than the gym's. Every place has exactly one,
 * and it carries no controls at all.
 */
export function HoldTypesPanel({ locationId }: HoldTypesPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [types, setTypes] = useState<HoldType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  /** The type whose deletion is being asked about, and whether it is under way. */
  const [deleting, setDeleting] = useState<HoldType | null>(null);
  const [removing, setRemoving] = useState(false);

  // Debounced, because the search runs server-side: a gym that came off free
  // text has more rows than fit one page, so a filter applied to what is in
  // hand would only ever search the page you are looking at.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    try {
      const result = await holdTypesService.list(locationId, {
        query,
        page,
        perPage: PAGE_SIZE,
      });
      setTypes(result.types);
      setTotal(result.total);
    } catch {
      toast({ title: t('gymAdmin.holdTypes.loadError'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [locationId, query, page, t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const replace = (updated: HoldType) =>
    setTypes((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));

  const failed = (error: unknown, title: string) =>
    toast({
      title,
      description: error instanceof Error ? error.message : t('errors.unexpectedError'),
      variant: 'destructive',
    });

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await holdTypesService.create(locationId, { name });
      setNewName('');
      setAdding(false);
      // Where it lands is the server's to say — the list is ordered and paged.
      load();
    } catch (error) {
      failed(error, t('gymAdmin.holdTypes.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Where a retired type's climbs go is a question with three answers, so it is
  // a dialog rather than a confirm — see HoldTypeDeleteDialog.
  const handleDelete = async (reassignTo: string | null) => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await holdTypesService.remove(locationId, deleting.id, reassignTo);
      setTypes((prev) => prev.filter((row) => row.id !== deleting.id));
      setDeleting(null);
      toast({ title: t('gymAdmin.holdTypes.deleted') });
      // The page is one row shorter, and where the climbs landed has a count
      // that has just gone up.
      if (page > 1 && types.length === 1) setPage(page - 1);
      else load();
    } catch (error) {
      failed(error, t('gymAdmin.holdTypes.deleteError'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{t('gymAdmin.holdTypes.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('gymAdmin.holdTypes.subtitle')}</p>
        </div>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="mr-1 size-4" />
            {t('gymAdmin.holdTypes.add')}
          </Button>
        )}
      </div>

      <div className="relative mb-3">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('gymAdmin.holdTypes.searchPlaceholder')}
          aria-label={t('gymAdmin.holdTypes.searchPlaceholder')}
          className="pl-9"
        />
      </div>

      {adding && (
        <div className="mb-3 flex items-center gap-2 rounded-md border p-2">
          <Input
            autoFocus
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAdd();
              }
              if (event.key === 'Escape') setAdding(false);
            }}
            placeholder={t('gymAdmin.holdTypes.namePlaceholder')}
            aria-label={t('gymAdmin.holdTypes.namePlaceholder')}
          />
          <Button size="sm" onClick={handleAdd} disabled={saving || !newName.trim()}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setNewName('');
            }}
            aria-label={t('common.cancel')}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : types.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground">
          {query ? t('gymAdmin.holdTypes.noMatch') : t('gymAdmin.holdTypes.empty')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {types.map((type) => (
            <HoldTypeRow
              key={type.id}
              type={type}
              locationId={locationId}
              onChanged={replace}
              onDelete={setDeleting}
              onError={failed}
            />
          ))}
        </ul>
      )}

      {lastPage > 1 && (
        <nav
          className="mt-3 flex items-center justify-between gap-3"
          aria-label={t('gymAdmin.holdTypes.pagination')}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft className="mr-1 size-4" />
            {t('gymAdmin.holdTypes.previousPage')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t('gymAdmin.holdTypes.pageOf', { page, pages: lastPage, total })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
          >
            {t('gymAdmin.holdTypes.nextPage')}
            <ChevronRight className="ml-1 size-4" />
          </Button>
        </nav>
      )}

      <HoldTypeDeleteDialog
        type={deleting}
        locationId={locationId}
        deleting={removing}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

interface HoldTypeRowProps {
  type: HoldType;
  locationId: string;
  onChanged: (type: HoldType) => void;
  onDelete: (type: HoldType) => void;
  onError: (error: unknown, title: string) => void;
}

function HoldTypeRow({ type, locationId, onChanged, onDelete, onError }: HoldTypeRowProps) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(type.name);

  useEffect(() => setName(type.name), [type.name]);

  // One field at a time, and each save sends only the field it changed, so two
  // of them in flight cannot overwrite each other's answer.
  const save = async (input: { name?: string; color?: string | null; color2?: string | null }) => {
    try {
      onChanged(await holdTypesService.update(locationId, type.id, input));
    } catch (error) {
      onError(error, t('gymAdmin.holdTypes.saveError'));
    }
  };

  // Reached on blur alone — Enter blurs the field rather than committing, so a
  // rename is one PATCH however it was confirmed.
  const commitName = async () => {
    const next = name.trim();
    setRenaming(false);
    if (!next || next === type.name) {
      setName(type.name);
      return;
    }
    await save({ name: next });
  };

  const count = type.climbs_count ?? 0;

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid="hold-type-row">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {renaming ? (
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                setName(type.name);
                setRenaming(false);
              }
            }}
            aria-label={t('gymAdmin.holdTypes.namePlaceholder')}
            className="h-8"
          />
        ) : (
          <>
            <span className="truncate text-sm font-medium">{type.name}</span>
            {type.editable ? (
              <button
                type="button"
                onClick={() => setRenaming(true)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('gymAdmin.holdTypes.rename', { name: type.name })}
              >
                <Pencil className="size-3.5" />
              </button>
            ) : (
              <Lock
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-label={t('gymAdmin.holdTypes.locked')}
              />
            )}
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <ColorSlot
          value={type.color}
          disabled={!type.editable}
          label={t('gymAdmin.holdTypes.color1', { name: type.name })}
          onChange={(color) => save({ color })}
        />
        <ColorSlot
          value={type.color2}
          disabled={!type.editable}
          label={t('gymAdmin.holdTypes.color2', { name: type.name })}
          onChange={(color2) => save({ color2 })}
        />
      </div>

      <span
        className="w-24 shrink-0 text-right text-xs text-muted-foreground"
        title={t('gymAdmin.holdTypes.climbsCountHint')}
      >
        {t('gymAdmin.holdTypes.climbsCount', { count })}
      </span>

      <div className="w-9 shrink-0">
        {type.editable && (
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={() => onDelete(type)}
            aria-label={t('gymAdmin.holdTypes.delete', { name: type.name })}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
    </li>
  );
}
